/* Smelu Premium Animation Foundation — GSAP + Lenis + IntersectionObserver
 * Stack-Spec: PREMIUM_ANIMATIONS.md §1, §6.1
 * Constraints: GPU-only props, prefers-reduced-motion respected, mobile (<990px) skips Hover/Tilt/Magnetic.
 * Lazy-init nach DOMContentLoaded. Idempotent (Shopify Section-Reload kompatibel).
 */
(function () {
  if (window.__smeluAnim) return;
  window.__smeluAnim = { ready: false, gsap: null, ScrollTrigger: null, lenis: null };

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var DESKTOP = window.matchMedia('(min-width: 990px)').matches;
  var GSAP_URL = 'https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/gsap.min.js';
  var ST_URL = 'https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/ScrollTrigger.min.js';
  var LENIS_URL = 'https://cdn.jsdelivr.net/npm/lenis@1.1.13/dist/lenis.min.js';

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src;
      s.async = false;
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  /* IntersectionObserver-only Reveal — funktioniert auch ohne GSAP (Fallback bei reduced-motion). */
  function initReveal() {
    var nodes = document.querySelectorAll('[data-anim]');
    if (!nodes.length) return;

    if (REDUCED) {
      nodes.forEach(function (el) { el.classList.add('smelu-anim-in'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var type = el.dataset.anim;
        var delay = parseFloat(el.dataset.animDelay || '0');

        if ((type === 'reveal-fade-up' || type === 'reveal-stagger') && window.__smeluAnim.gsap) {
          var g = window.__smeluAnim.gsap;
          if (type === 'reveal-stagger') {
            var children = el.querySelectorAll('[data-anim-child]');
            g.to(children, {
              opacity: 1,
              y: 0,
              duration: DESKTOP ? 0.5 : 0.4,
              stagger: DESKTOP ? 0.08 : 0.05,
              ease: 'power2.out',
              delay: delay,
            });
          } else {
            g.to(el, {
              opacity: 1,
              y: 0,
              duration: DESKTOP ? 0.6 : 0.4,
              ease: 'power3.out',
              delay: delay,
            });
          }
          el.classList.add('smelu-anim-in');
        } else {
          el.classList.add('smelu-anim-in');
        }
        io.unobserve(el);
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.15 });

    nodes.forEach(function (el) { io.observe(el); });
  }

  /* Image-Parallax — Desktop only, ScrollTrigger-driven. */
  function initParallax() {
    if (!DESKTOP || REDUCED || !window.__smeluAnim.gsap || !window.__smeluAnim.ScrollTrigger) return;
    var g = window.__smeluAnim.gsap;
    var nodes = document.querySelectorAll('[data-anim-parallax]');
    nodes.forEach(function (el) {
      var factor = parseFloat(el.dataset.animParallax || '0.3');
      g.to(el, {
        yPercent: -factor * 30,
        ease: 'none',
        scrollTrigger: {
          trigger: el.closest('[data-anim-parallax-container]') || el,
          start: 'top bottom',
          end: 'bottom top',
          scrub: 0.5,
        },
      });
    });
  }

  /* Magnetic Buttons — Desktop only. */
  function initMagnetic() {
    if (!DESKTOP || REDUCED) return;
    var nodes = document.querySelectorAll('[data-anim-magnetic]');
    nodes.forEach(function (el) {
      var bounds = null;
      var raf = null;
      function onMove(e) {
        if (!bounds) bounds = el.getBoundingClientRect();
        var dx = e.clientX - (bounds.left + bounds.width / 2);
        var dy = e.clientY - (bounds.top + bounds.height / 2);
        var dist = Math.sqrt(dx * dx + dy * dy);
        if (dist > 80) return;
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(function () {
          el.style.transform = 'translate(' + (dx * 0.18) + 'px,' + (dy * 0.18) + 'px)';
        });
      }
      function onLeave() {
        bounds = null;
        if (raf) cancelAnimationFrame(raf);
        el.style.transform = '';
      }
      el.addEventListener('mousemove', onMove);
      el.addEventListener('mouseleave', onLeave);
    });
  }

  /* Number-CountUp on enter (single-shot). */
  function initCountUp() {
    if (REDUCED) return;
    var nodes = document.querySelectorAll('[data-anim-countup]');
    if (!nodes.length || !window.__smeluAnim.gsap) return;
    var g = window.__smeluAnim.gsap;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var target = parseFloat(el.dataset.animCountup || el.textContent.replace(/[^\d.]/g, '')) || 0;
        var obj = { v: 0 };
        g.to(obj, {
          v: target,
          duration: 1.8,
          ease: 'power2.out',
          onUpdate: function () { el.textContent = Math.round(obj.v).toLocaleString('de-DE'); },
        });
        io.unobserve(el);
      });
    }, { threshold: 0.4 });
    nodes.forEach(function (el) { io.observe(el); });
  }

  /* Lenis smooth-scroll — DEAKTIVIERT auf reduced-motion + Designmode. */
  function initLenis() {
    /* 15.08.2026: nur Desktop. Lenis ersetzt das native Scrollen durch einen
     * requestAnimationFrame-Loop im Main Thread. Auf schwaecheren Mobilgeraeten
     * fuehlt sich das traege an statt souveraen. Die Smooth-Anchor-Funktion hat
     * einen nativen Fallback (window.scrollTo behavior:smooth), es geht nichts verloren. */
    if (!DESKTOP || REDUCED || !window.Lenis || (window.Shopify && window.Shopify.designMode)) return;
    var lenis = new window.Lenis({
      duration: 1.0,
      easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); },
      smoothWheel: true,
      touchMultiplier: 1.5,
    });
    function raf(time) { lenis.raf(time); requestAnimationFrame(raf); }
    requestAnimationFrame(raf);
    window.__smeluAnim.lenis = lenis;
    if (window.__smeluAnim.ScrollTrigger) {
      lenis.on('scroll', window.__smeluAnim.ScrollTrigger.update);
    }
  }

  /* ──────────────────────────────────────────────────────────────────
     Globaler Smooth-Anchor-Handler — store-übergreifend
     Jeder Klick auf <a href="#xyz"> wird in einen butterweichen Scroll
     zur Ziel-Section verwandelt statt instant zu teleportieren.
     Berücksichtigt:
       · Lenis (wenn aktiv) für GSAP-konsistente Animation
       · Sticky-Header-Offset (80px)
       · prefers-reduced-motion → instant jump
       · URL-Hash-Update für Browser-Back/Forward
       · Speziallinks (target=_blank, download=, data-no-smooth) übersprungen
     ────────────────────────────────────────────────────────────────── */
  function initSmoothAnchors() {
    if (window.__smeluAnim._smoothAnchorsBound) return;
    window.__smeluAnim._smoothAnchorsBound = true;

    var HEADER_OFFSET = 80;

    document.addEventListener('click', function (e) {
      // Modifier-Keys → User will Tab-öffnen, nicht scrollen
      if (e.defaultPrevented) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;

      var link = e.target && e.target.closest && e.target.closest('a[href]');
      if (!link) return;

      var href = link.getAttribute('href');
      if (!href || href.length < 2 || href.charAt(0) !== '#') return;

      // Opt-out via data-no-smooth oder target=_blank/download
      if (link.hasAttribute('data-no-smooth')) return;
      if (link.target && link.target !== '' && link.target !== '_self') return;
      if (link.hasAttribute('download')) return;

      // Element finden — try ID, then named anchor
      var id = href.slice(1);
      var target = document.getElementById(id) || document.querySelector('a[name="' + id + '"]');
      if (!target) return;

      e.preventDefault();

      // Ziel-Y-Position berechnen (mit scroll-margin-top respect)
      function getTargetY() {
        var rect = target.getBoundingClientRect();
        var marginTop = parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
        var topInDoc = rect.top + (window.pageYOffset || document.documentElement.scrollTop);
        return Math.max(0, topInDoc - (marginTop || HEADER_OFFSET));
      }

      var lenis = window.__smeluAnim && window.__smeluAnim.lenis;
      var reduced = REDUCED;

      // scroll-margin-top am Target respektieren (statt globaler HEADER_OFFSET).
      // Wenn margin-top: 0 → exakt zum Target, sonst margin-top als Offset.
      var targetMarginTop = parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
      var effectiveOffset = targetMarginTop > 0 ? targetMarginTop : HEADER_OFFSET;

      if (reduced) {
        // Instant Jump — kein Effekt für motion-sensitive Users
        window.scrollTo(0, getTargetY());
      } else if (lenis && typeof lenis.scrollTo === 'function') {
        lenis.scrollTo(target, {
          offset: -effectiveOffset,
          duration: 1.0,
          easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); }
        });
      } else {
        // Fallback: native smooth-scroll
        window.scrollTo({ top: getTargetY(), behavior: 'smooth' });
      }

      // URL-Hash aktualisieren ohne erneuten Jump-Trigger
      if (window.history && window.history.pushState) {
        window.history.pushState(null, '', href);
      }
    }, false);
  }

  function bootAll() {
    initReveal();
    initParallax();
    initMagnetic();
    initCountUp();
    initLenis();
    initSmoothAnchors();
    window.__smeluAnim.ready = true;
    document.dispatchEvent(new CustomEvent('smelu:anim-ready'));
  }

  function init() {
    /* 15.08.2026 — KRITISCHER FIX.
     * Vorher wurde bootAll() erst NACH dem Laden von GSAP + ScrollTrigger + Lenis
     * von cdn.jsdelivr.net aufgerufen. Da [data-anim] in smelu-base.css auf
     * opacity:0 startet, blieb der komplette Seiteninhalt so lange unsichtbar,
     * wie der fremde CDN brauchte. Das war der sichtbare "Ladescreen ohne Inhalte".
     * initReveal() kommt ohne GSAP aus (es faellt intern auf die CSS-Transition
     * zurueck) und wird deshalb jetzt sofort gestartet. GSAP verfeinert danach nur.
     * Beide Aufrufe sind idempotent: initReveal nutzt io.unobserve, initSmoothAnchors
     * hat ein _smoothAnchorsBound-Flag. Der spaetere bootAll() schadet also nicht. */
    initReveal();
    initSmoothAnchors();

    if (REDUCED) {
      bootAll();
      return;
    }
    Promise.all([loadScript(GSAP_URL), loadScript(ST_URL), loadScript(LENIS_URL)])
      .then(function () {
        if (window.gsap) {
          window.__smeluAnim.gsap = window.gsap;
          if (window.ScrollTrigger) {
            window.gsap.registerPlugin(window.ScrollTrigger);
            window.__smeluAnim.ScrollTrigger = window.ScrollTrigger;
          }
        }
        bootAll();
      })
      .catch(function () {
        bootAll();
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function () {
    initReveal();
    initParallax();
    initMagnetic();
    initCountUp();
  });
})();
